import cx from 'clsx';
import * as React from 'react';

import AssetLayer from '@/components/AssetLayer/AssetLayer';
import {
  LayersWrapper,
  QuicksilverTileOutfitType,
} from '@/components/game-tile';
import JackpotLayerGameTile from '@/components/game-tile/JackpotLayer/JackpotLayerGameTile';
import LiveTableLayer from '@/components/game-tile/LiveTableLayer/LiveTableLayer';
import SashLayer from '@/components/game-tile/SashLayer/SashLayer';
import GameLink from '@/components/GameLink';
import { Info } from '@/components/icons';
import {
  INFO_LABEL,
  LARGE_BREAKPOINT,
  MEDIUM_BREAKPOINT,
  SMALL_BREAKPOINT,
} from '@/components/modular-grid/constants';
import { BannerType } from '@/components/modular-grid/types';
import { prepareGameInfoTooltip } from '@/components/modular-grid/utils';
import Spinner from '@/components/Spinner';
import { useWindowSize } from '@/hooks/useWindowSize';
import { CategoryFinancialWebAttributes } from '@/types/category/categoryTypes';
import { ImageType, JackpotType, Table } from '@/types/gameTypes';

import styles from './Banner.module.scss';

interface BannerComponentProps {
  ghost?: boolean;
  asset?: {
    type: BannerType;
  } & ImageType;
  smallLargeAsset?: {
    type: BannerType;
  } & ImageType;
  mediumAsset?: {
    type: BannerType;
  } & ImageType;
  bannerButton?: string;
  bannerDescription?: string;
  gameBannerTitle?: string;
  gameLink?: {
    gameDetailsPath?: string;
    url: string;
    target: string;
    onClick?: (event: React.MouseEvent<HTMLAnchorElement>) => void;
    shouldMapUrl?: boolean;
    integration?: string;
    notIndexed?: boolean;
  };
  info: {
    visible?: boolean;
    onClick?: (event: React.MouseEvent) => void;
    gameInfoTooltip?: string;
  };
  sashLayer?: {
    sash: string;
    background: string;
    outfit: QuicksilverTileOutfitType;
  };
  tableLayer?: Table;
  jackpotLayer?: JackpotType;
  isSingle?: boolean;
  personalization?: {
    selector: string;
    slug?: string;
    rendered?: boolean;
  };
  cloned?: boolean;
  index?: number;
  categoryAttributes?: CategoryFinancialWebAttributes;
  buttonPosition?: string;
}

const createBannerAssets = (
  smallLargeAsset?: {
    type: BannerType;
  } & ImageType,
  mediumAsset?: {
    type: BannerType;
  } & ImageType,
  // TODO - tmp solution for banner on mobile
  forcedSmallAsset?: {
    type: BannerType;
  } & ImageType
) =>
  smallLargeAsset &&
  mediumAsset && [
    {
      max: SMALL_BREAKPOINT.max,
      src: forcedSmallAsset ? forcedSmallAsset.source : smallLargeAsset.source,
    },
    {
      min: MEDIUM_BREAKPOINT.min,
      max: MEDIUM_BREAKPOINT.max,
      src: mediumAsset.source,
    },
    {
      min: LARGE_BREAKPOINT.min,
      src: smallLargeAsset.source,
    },
  ];

const BannerComponent: React.FC<BannerComponentProps> = ({
  asset,
  smallLargeAsset,
  mediumAsset,
  gameLink,
  info,
  ghost,
  sashLayer,
  bannerButton,
  bannerDescription,
  gameBannerTitle,
  isSingle,
  tableLayer,
  personalization,
  cloned,
  jackpotLayer,
  categoryAttributes,
  index = 0,
  buttonPosition,
}) => {
  const windowWidth = useWindowSize().width;
  const preparedGameInfoTooltip = prepareGameInfoTooltip(
    info.gameInfoTooltip,
    gameBannerTitle || ''
  );

  return !personalization ? (
    <div className={styles.bannerComponentWrapper}>
      <div className={styles.slotWrapper} data-testid="banner-slot">
        <div
          className={cx(
            smallLargeAsset ? styles.fileBanner : styles.file,
            ghost && styles.ghost
          )}
        >
          <LayersWrapper>
            <AssetLayer
              type={
                smallLargeAsset ? smallLargeAsset.type : asset?.type || 'image'
              }
              sizedSources={createBannerAssets(
                smallLargeAsset,
                mediumAsset,
                asset
              )}
              imageSrc={asset?.source}
              alt={asset?.alt}
              isLazy={true}
              loadingAnimation={false}
              videoAttributes={{
                autoPlay: true,
                loop: true,
                playsInline: true,
                muted: true,
              }}
              videoSrc={asset && asset.source.normal}
              // TODO - tmp solution for banner on mobile
              imageAttributes={
                bannerDescription && windowWidth <= SMALL_BREAKPOINT.max
                  ? { style: { maxWidth: '100%' } }
                  : {}
              }
              rounded={
                bannerDescription
                  ? windowWidth > SMALL_BREAKPOINT.max
                    ? 'top'
                    : 'all'
                  : 'all'
              }
            />
          </LayersWrapper>
          {bannerButton && (
            <div
              className={cx(
                styles.button,
                isSingle
                  ? styles.buttonSingle
                  : buttonPosition?.toLowerCase() === 'left'
                  ? styles.buttonPositionLeft
                  : styles.buttonPositionRight
              )}
            >
              {bannerButton}
            </div>
          )}
          {gameLink && (
            <GameLink
              url={gameLink.url}
              target={gameLink.target}
              shouldMapUrl={gameLink.shouldMapUrl}
              integration={gameLink.integration}
              onClick={gameLink.onClick}
              notIndexed={gameLink.notIndexed}
              categoryAttributes={categoryAttributes}
            />
          )}
          {tableLayer && (
            <LiveTableLayer table={tableLayer} outfit={'banner'} />
          )}
          {info && info.visible && (
            <a
              className={cx(styles.info, styles.link)}
              target="_blank"
              rel="noopener noreferrer"
              href={gameLink?.gameDetailsPath}
              title={preparedGameInfoTooltip}
            >
              <div
                className={styles.infoWrapper}
                data-testid="game-info"
                onClick={e => {
                  e.preventDefault();
                  info.onClick && info.onClick(e);
                }}
              >
                <Info altText={preparedGameInfoTooltip} />
                <p className={styles.infoLabel}>{INFO_LABEL}</p>
              </div>
            </a>
          )}
          {jackpotLayer && !tableLayer && (
            <JackpotLayerGameTile {...jackpotLayer} />
          )}
          {sashLayer && <SashLayer {...sashLayer} />}
        </div>
        {bannerDescription && (
          <div className={styles.detailsOverlay}>
            <span>{bannerDescription}</span>
          </div>
        )}
      </div>
    </div>
  ) : (
    <div className={styles.bannerComponentWrapper}>
      <div
        data-testid="banner-slot"
        className={cx(
          cloned
            ? personalization.selector.replace('.', '') + '_clone'
            : personalization.selector.replace('.', ''),
          personalization.rendered !== true && styles.hidden
        )}
      />
      {personalization.rendered === undefined ? (
        <div className={cx(styles.spinnerSlotWrapper)}>
          <div className={cx(styles.ghost)}>
            <div className={styles.spinnerWrapper}>
              <Spinner
                animation={
                  windowWidth <= SMALL_BREAKPOINT.max
                    ? index === 0
                    : !cloned && index < 1
                }
              />
              <h3>Fetching our latest offers</h3>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default BannerComponent;
